import omit from 'ramda/es/omit';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { ReactEditor, useSlateStatic, useSlateSelection } from 'slate-react';
import { css } from 'styled-components';
import { Range as SlateRange } from 'slate';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useReporterStyles } from 'hooks/useReporterStyles';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags';
import { useRecoilValue } from 'recoil';
import { proceduresRelevanceState } from '../../Reporter/state';
import { PICKLIST_PLUGIN_ID } from '../plugins/picklist/types';
import { MACRO_PLACEHOLDER_PLUGIN_ID } from '../plugins/macroPlaceholder/types';
import { useReporterState } from '../hooks/useReporter';
import type { AllReporterVariant } from '../hooks/useReporter';
import { isSelectionContainedInNode } from '../utils/isSelectionContainedInNode';
import { find } from '../utils/find';
import { useFocusMode } from 'hooks/useFocusMode';
import { telemetryContextFor } from 'modules/Apollo/types';
import type { VirtualElement } from '@popperjs/core';
import { type RangeSnapshot, setReferenceElementIfRangeChanged } from './utils/rangeSnapshot';
import { useCoalescedAnimationFrame } from '../hooks/useCoalescedAnimationFrame';

const CURSOR_HEIGHT_PADDING = 2;

export type CursorIndicatorProps = {
  containerWidth: number;
  containerHeight: number;
  color: string;
  isAddendumEditor?: boolean;
};

export const shouldHideCursorIndicator = ({
  editor,
  selection,
  variant,
}: {
  editor: Parameters<typeof find>[0];
  selection: SlateRange;
  variant: AllReporterVariant;
}): boolean => {
  if (!SlateRange.isCollapsed(selection)) {
    return false;
  }

  // If you are in the template or macro editor, we check to see if your selection is currently entirely contained within
  // a picklist. If it is, that means that you are not able to interact with the picklist as you need to edit options inside the pane. To make this clear
  // to the user, we hide the cursor indicator until the selection is no longer contained within the picklist.
  if (['template', 'fragment'].includes(variant)) {
    const picklistEntry = find(editor, (n) => n.type === PICKLIST_PLUGIN_ID);

    if (picklistEntry != null && isSelectionContainedInNode(editor, picklistEntry[1], selection)) {
      return true;
    }
  }

  // Macro placeholders rendered as chips are non-editable/void-like for cursor UX, so hide the visual caret.
  const macroPlaceholderEntry = find(editor, (n) => n.type === MACRO_PLACEHOLDER_PLUGIN_ID, {
    voids: true,
  });

  if (
    macroPlaceholderEntry != null &&
    isSelectionContainedInNode(editor, macroPlaceholderEntry[1], selection)
  ) {
    return true;
  }

  return false;
};

export function CursorIndicator(props: CursorIndicatorProps): React.ReactElement | null {
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [referenceElement, setReferenceElement] = useState<globalThis.Range | null>(null);
  const [animationKey, setAnimationKey] = useState(0);
  const previousRangeSnapshot = useRef<Nullable<RangeSnapshot>>(null);
  const prevReferenceElementRef = useRef<globalThis.Range | null>(null);
  const referenceElementRef = useRef<globalThis.Range | null>(null);
  referenceElementRef.current = referenceElement;
  // Stable object so Popper never recreates its instance on cursor moves — only on mount/unmount.
  const popperReferenceRef = useRef<VirtualElement>({
    getBoundingClientRect: () =>
      referenceElementRef.current?.getBoundingClientRect() ?? new DOMRect(),
    get contextElement() {
      const container = referenceElementRef.current?.commonAncestorContainer;
      if (container == null) return undefined;
      return container instanceof Element ? container : (container.parentElement ?? undefined);
    },
  });
  const popperReference = referenceElement != null ? popperReferenceRef.current : null;
  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const reporterStyles = useReporterStyles(data?.me?.reporterSettings);
  const { styles, state, update } = usePopper(popperReference, popperElement, {
    placement: 'right',
    modifiers: [{ name: 'offset', options: { offset: [0, 0] } }],
  });
  const proceduresScope = useRecoilValue(proceduresRelevanceState);

  const [isCursorImprovementsEnabled] = useFeatureFlagEnabled(FF.REPORTER_CURSOR_IMPROVEMENTS);
  const editor = useSlateStatic();
  const selection = useSlateSelection();
  const { variant } = useReporterState();
  const { isFocusModeOn } = useFocusMode({ context: APOLLO_QUERY_CONTEXT });
  const updateReferenceElement = useCallback(() => {
    const setReferenceElementIfChanged = (nextRange: globalThis.Range | null) =>
      setReferenceElementIfRangeChanged({
        nextRange,
        previousRangeSnapshotRef: previousRangeSnapshot,
        setReferenceElement,
      });

    if (selection == null || editor == null) {
      setReferenceElementIfChanged(null);
      return;
    }

    if (SlateRange.isCollapsed(selection)) {
      try {
        if (shouldHideCursorIndicator({ editor, selection, variant })) {
          setReferenceElementIfChanged(null);
          return;
        }
        const domRange = ReactEditor.toDOMRange(editor, selection);
        setReferenceElementIfChanged(domRange);
      } catch {
        const domSelection = window.getSelection();
        if (domSelection != null && domSelection.rangeCount > 0) {
          const domRange = domSelection.getRangeAt(0);
          setReferenceElementIfChanged(domRange);
        }
      }
    } else {
      setReferenceElementIfChanged(null);
    }
  }, [editor, selection, variant]);
  const { schedule: scheduleUpdateReferenceElement } =
    useCoalescedAnimationFrame(updateReferenceElement);

  useEffect(() => {
    updateReferenceElement();
    // `reporterStyles`/`proceduresScope` are intentional deps: changing them can shift
    // the editor layout, so the cursor reference element must be re-measured even though
    // they aren't read directly here.
  }, [updateReferenceElement, reporterStyles, proceduresScope]);

  useEffect(() => {
    scheduleUpdateReferenceElement();
  }, [props.containerHeight, props.containerWidth, scheduleUpdateReferenceElement]);

  useEffect(() => {
    // Track browser selection, scrolling, and resizing without a perpetual RAF loop.
    document.addEventListener('selectionchange', scheduleUpdateReferenceElement);
    window.addEventListener('scroll', scheduleUpdateReferenceElement, true);
    window.addEventListener('resize', scheduleUpdateReferenceElement);

    // Also react to editor DOM changes (delayed reflows) when the node is resolvable.
    let mutationObserver: MutationObserver | undefined;
    try {
      const editableElement = ReactEditor.toDOMNode(editor, editor);
      mutationObserver = new MutationObserver(scheduleUpdateReferenceElement);
      mutationObserver.observe(editableElement, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    } catch {
      // Editor DOM node not attached yet; global listeners still keep the cursor aligned.
    }

    return () => {
      mutationObserver?.disconnect();
      document.removeEventListener('selectionchange', scheduleUpdateReferenceElement);
      window.removeEventListener('scroll', scheduleUpdateReferenceElement, true);
      window.removeEventListener('resize', scheduleUpdateReferenceElement);
    };
  }, [editor, scheduleUpdateReferenceElement]);

  useEffect(() => {
    const prev = prevReferenceElementRef.current;
    prevReferenceElementRef.current = referenceElement;
    if (prev != null && referenceElement != null && referenceElement !== prev) {
      setAnimationKey((k) => k + 1);
    }
    if (referenceElement != null && update != null) {
      void update();
    }
  }, [referenceElement, update]);

  if (referenceElement == null || isFocusModeOn) {
    return null;
  }

  let containerElement: Node | null = referenceElement.commonAncestorContainer;

  // If the container is a text node, get its parent element
  if (containerElement.nodeType === Node.TEXT_NODE) {
    containerElement = containerElement.parentNode ?? containerElement;
  }
  if (!(containerElement instanceof Element)) {
    return null;
  }

  const fontSize = parseInt(
    window.getComputedStyle(containerElement).fontSize.replace('px', ''),
    10
  );
  const popperStyles = omit(['transform'], styles.popper || {});
  const popperOffsetX = Math.round(state?.modifiersData?.popperOffsets?.x ?? 0);
  const popperOffsetY = Math.round(state?.modifiersData?.popperOffsets?.y ?? 0);

  return (
    <div
      data-testid="cursor-indicator"
      ref={setPopperElement}
      {...(state?.attributes?.popper != null ? state.attributes.popper : {})}
      style={{
        transform: `translate(${popperOffsetX}px, ${popperOffsetY}px)`,
        ...popperStyles,
        width: isCursorImprovementsEnabled ? '2px' : '1px',
      }}
      css={css`
        @keyframes cursor-blink {
          from {
            opacity: 1;
          }
          20% {
            opacity: 1;
          }
          28% {
            opacity: 0;
          }
          72% {
            opacity: 0;
          }
          80% {
            opacity: 1;
          }
          to {
            opacity: 1;
          }
        }

        &:has(~ [contenteditable]:focus) > div {
          animation: cursor-blink 0.6s linear infinite alternate;
        }

        z-index: 0;
        height: ${fontSize + CURSOR_HEIGHT_PADDING}px;
      `}
    >
      <div
        key={animationKey}
        data-testid="cursor-indicator-inner"
        css={css`
          background-color: ${props.color};
          display: flex;
          align-content: center;
          height: 100%;
          width: ${isCursorImprovementsEnabled ? '2px' : '1px'};
        `}
      />
    </div>
  );
}

CursorIndicator.displayName = 'CursorIndicator';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(CursorIndicator);
